import { useState, useEffect, useRef } from 'react';

interface ViewTransform {
  x: number;
  y: number;
  scale: number;
}

interface ZoomMomentum {
  velocity: number;
  mouseX: number;
  mouseY: number;
}

interface ZoomTransition {
  isTransitioning: boolean;
  startScale: number;
  targetScale: number;
  startTime: number;
  centerX?: number;
  centerY?: number;
}

interface UseZoomProps {
  viewBox: { width: number; height: number }; // SVG viewBox dimensions
  initialTransform?: ViewTransform;
}

interface UseZoomReturn {
  viewTransform: ViewTransform;
  setViewTransform: (transform: ViewTransform) => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleZoomReset: () => void;
  handleZoomToFit: (nodes: Array<{ x: number; y: number }>) => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  svgRef: React.RefObject<SVGSVGElement>;
}

export function useZoom({ 
  viewBox,
  initialTransform = { x: 0, y: 0, scale: 1 } 
}: UseZoomProps): UseZoomReturn {
  const svgRef = useRef<SVGSVGElement>(null);
  const MIN_SCALE = 0.1;
  const MAX_SCALE = 5;

  const [viewTransform, setViewTransform] = useState<ViewTransform>(initialTransform);
  const [zoomMomentum, setZoomMomentum] = useState<ZoomMomentum>({ velocity: 0, mouseX: 0, mouseY: 0 });
  const [zoomTransition, setZoomTransition] = useState<ZoomTransition | null>(null);

  const smoothZoomTo = (targetScale: number, centerX?: number, centerY?: number) => {
    const clampedScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, targetScale));
    if (Math.abs(clampedScale - viewTransform.scale) < 0.001) return;

    setZoomTransition({
      isTransitioning: true,
      startScale: viewTransform.scale,
      targetScale: clampedScale,
      startTime: Date.now(),
      centerX,
      centerY
    });
  };

  const handleZoomIn = () => {
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (svgRect) {
      // Zoom towards center when using buttons
      const centerX = svgRect.width / 2;
      const centerY = svgRect.height / 2;
      smoothZoomTo(viewTransform.scale * 1.5, centerX, centerY);
    }
  };

  const handleZoomOut = () => {
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (svgRect) {
      // Zoom towards center when using buttons
      const centerX = svgRect.width / 2;
      const centerY = svgRect.height / 2;
      smoothZoomTo(viewTransform.scale / 1.5, centerX, centerY);
    }
  };

  const handleZoomReset = () => {
    setViewTransform({
      x: 0,
      y: 0,
      scale: 1
    });
    setZoomMomentum({ velocity: 0, mouseX: 0, mouseY: 0 });
    setZoomTransition(null);
  };

  const handleZoomToFit = (nodes: Array<{ x: number; y: number }>) => {
    if (!svgRef.current || nodes.length === 0) return;

    // Find the bounds of all nodes
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    for (const node of nodes) {
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, node.y);
      maxX = Math.max(maxX, node.x);
      maxY = Math.max(maxY, node.y);
    }

    // Add some padding around the nodes
    const padding = 100;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const contentCenterX = minX + contentWidth / 2;
    const contentCenterY = minY + contentHeight / 2;

    // Calculate the scale to fit the content in the viewBox
    const scaleX = viewBox.width / contentWidth;
    const scaleY = viewBox.height / contentHeight;
    const scale = Math.min(scaleX, scaleY, MAX_SCALE); // Don't exceed max zoom

    // Calculate the transform to center the content
    const x = viewBox.width / 2 - contentCenterX * scale;
    const y = viewBox.height / 2 - contentCenterY * scale;

    // Apply the transform with smooth transition
    setZoomTransition({
      isTransitioning: true,
      startScale: viewTransform.scale,
      targetScale: scale,
      startTime: Date.now()
    });

    // Set the final transform (the transition animation will handle the smooth change)
    setViewTransform({ x, y, scale });
    setZoomMomentum({ velocity: 0, mouseX: 0, mouseY: 0 });
  };

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!svgRef.current) return;

    // Use SVG's built-in coordinate system conversion (same as useDrag)
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    
    // Transform through all matrix transformations to get SVG coordinates
    const screenCTM = svg.getScreenCTM();
    if (!screenCTM) return;
    
    const svgPoint = pt.matrixTransform(screenCTM.inverse());
    
    // Increased sensitivity for faster zoom - 5% per wheel tick
    const wheelSensitivity = 0.05;
    const zoomDirection = event.deltaY > 0 ? -1 : 1;
    const zoomDelta = zoomDirection * wheelSensitivity;
    
    // Update momentum with accurate SVG coordinate mouse position
    setZoomMomentum(prev => ({
      velocity: prev.velocity + zoomDelta,
      mouseX: svgPoint.x,
      mouseY: svgPoint.y
    }));
  };

  // Animation effects for smooth zoom transitions
  useEffect(() => {
    if (zoomTransition?.isTransitioning) {
      const animateTransition = () => {
        const now = Date.now();
        const elapsed = now - zoomTransition.startTime;
        const duration = 150; // 150ms transition - faster
        
        if (elapsed >= duration) {
          // Transition complete
          setViewTransform(prev => {
            if (!zoomTransition.centerX || !zoomTransition.centerY) {
              return { ...prev, scale: zoomTransition.targetScale };
            }
            
            // Convert screen coordinates to SVG coordinates for center calculation
            const svgRect = svgRef.current?.getBoundingClientRect();
            if (!svgRect) return prev;
            
            const scaleX = viewBox.width / svgRect.width;
            const scaleY = viewBox.height / svgRect.height;
            
            const svgCenterX = zoomTransition.centerX * scaleX;
            const svgCenterY = zoomTransition.centerY * scaleY;
            
            // Calculate the point in the transformed coordinate system
            const worldX = (svgCenterX - prev.x) / prev.scale;
            const worldY = (svgCenterY - prev.y) / prev.scale;
            
            // Calculate new transform to keep the world point under the mouse
            const newX = svgCenterX - worldX * zoomTransition.targetScale;
            const newY = svgCenterY - worldY * zoomTransition.targetScale;

            return {
              x: newX,
              y: newY,
              scale: zoomTransition.targetScale
            };
          });
          setZoomTransition(null);
        } else {
          // Continue transition with easing
          const progress = elapsed / duration;
          const easeProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
          
          setViewTransform(prev => {
            if (!zoomTransition.centerX || !zoomTransition.centerY) {
              const currentScale = zoomTransition.startScale + 
                (zoomTransition.targetScale - zoomTransition.startScale) * easeProgress;
              return { ...prev, scale: currentScale };
            }
            
            const currentScale = zoomTransition.startScale + 
              (zoomTransition.targetScale - zoomTransition.startScale) * easeProgress;
            
            // Convert screen coordinates to SVG coordinates
            const svgRect = svgRef.current?.getBoundingClientRect();
            if (!svgRect) return prev;
            
            const scaleX = viewBox.width / svgRect.width;
            const scaleY = viewBox.height / svgRect.height;
            
            const svgCenterX = zoomTransition.centerX * scaleX;
            const svgCenterY = zoomTransition.centerY * scaleY;
            
            const worldX = (svgCenterX - prev.x) / prev.scale;
            const worldY = (svgCenterY - prev.y) / prev.scale;
            
            const newX = svgCenterX - worldX * currentScale;
            const newY = svgCenterY - worldY * currentScale;

            return {
              x: newX,
              y: newY,
              scale: currentScale
            };
          });
          
          requestAnimationFrame(animateTransition);
        }
      };
      
      requestAnimationFrame(animateTransition);
    }
  }, [zoomTransition, viewBox, svgRef]);

  // Momentum animation effect
  useEffect(() => {
    if (Math.abs(zoomMomentum.velocity) < 0.001) return;

    const animationFrame = requestAnimationFrame(() => {      
      setViewTransform(prev => {
        // Make zoom speed relative to current scale for consistent feel
        const scaleDelta = zoomMomentum.velocity * prev.scale * 0.08;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev.scale + scaleDelta));
        
        // Calculate the point in the world coordinate system that should remain under the mouse
        const worldX = (zoomMomentum.mouseX - prev.x) / prev.scale;
        const worldY = (zoomMomentum.mouseY - prev.y) / prev.scale;
        
        // Calculate new transform to keep that world point under the mouse
        const newX = zoomMomentum.mouseX - worldX * newScale;
        const newY = zoomMomentum.mouseY - worldY * newScale;

        return {
          x: newX,
          y: newY,
          scale: newScale
        };
      });

      // Faster decay momentum for more responsive feel
      setZoomMomentum(prev => ({
        ...prev,
        velocity: prev.velocity * 0.75
      }));
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [zoomMomentum.velocity]);

  // Attach wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    svg.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      svg.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  return {
    viewTransform,
    setViewTransform,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleZoomToFit,
    canZoomIn: viewTransform.scale < MAX_SCALE,
    canZoomOut: viewTransform.scale > MIN_SCALE,
    svgRef,
  };
}